import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from '@core/icons-react';
import { DropdownContainer } from '../shared';

const cx = (...classes: (string | boolean | undefined)[]) => 
  classes.filter(Boolean).join(' ');

export interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

export interface DateDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onDateRangeChange?: (range: DateRange) => void;
  onPresetSelect?: (range: DateRange) => void;
  initialRange?: DateRange;
  anchorRef?: React.RefObject<HTMLElement | null>;
  showPresets?: boolean;
  className?: string;
}

const PRESET_RANGES = [
  { id: 'week-to-date', label: 'Week to date' },
  { id: 'month-to-date', label: 'Month to date' },
  { id: 'last-7', label: 'Last 7 days' },
  { id: 'last-14', label: 'Last 14 days' },
  { id: 'last-30', label: 'Last 30 days' },
  { id: 'last-6-months', label: 'Last 6 Months' },
  { id: 'last-year', label: 'Last 12 Months' },
  { id: 'all-time', label: 'All Time' }
];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export function DateDropdown({
  isOpen,
  onClose,
  onDateRangeChange,
  onPresetSelect,
  initialRange,
  anchorRef,
  showPresets = true,
  className
}: DateDropdownProps) {
  const [selectedRange, setSelectedRange] = useState<DateRange>(
    initialRange || { startDate: null, endDate: null }
  );
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const today = new Date();
    const year = today.getUTCFullYear();
    const month = today.getUTCMonth();
    return new Date(Date.UTC(year, month - 1, 1));
  });
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);

  // Update selected range when calendar opens with a date range
  useEffect(() => {
    if (initialRange && isOpen) {
      setSelectedRange(initialRange);
      
      if (initialRange.startDate) {
        const year = initialRange.startDate.getUTCFullYear();
        const month = initialRange.startDate.getUTCMonth();
        const startMonthDate = new Date(Date.UTC(year, month, 1));
        
        // If both dates are in the same month, show previous month on left
        if (initialRange.endDate) {
          if (initialRange.startDate.getUTCMonth() === initialRange.endDate.getUTCMonth() &&
              initialRange.startDate.getUTCFullYear() === initialRange.endDate.getUTCFullYear()) {
            setCurrentMonth(new Date(Date.UTC(year, month - 1, 1)));
          } else {
            setCurrentMonth(startMonthDate);
          }
        } else {
          setCurrentMonth(startMonthDate);
        }
      }
    }
  }, [isOpen, initialRange]);


  const getStartOfWeekDate = () => {
    const today = new Date();
    const dayUTC = today.getUTCDay();
    const dateUTC = today.getUTCDate();
    const diff = dateUTC - dayUTC;
    return new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), diff, 0, 0, 0, 0));
  };

  const getStartOfMonthDate = () => {
    const today = new Date();
    return new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1, 0, 0, 0, 0));
  };

  const handlePresetClick = (presetId: string) => {
    setActivePreset(presetId);
    const today = new Date();
    const utcYear = today.getUTCFullYear();
    const utcMonth = today.getUTCMonth();
    const utcDate = today.getUTCDate();
    
    const endOfToday = new Date(Date.UTC(utcYear, utcMonth, utcDate, 23, 59, 59, 999));
    let range: DateRange = { startDate: null, endDate: endOfToday };

    switch (presetId) {
      case 'week-to-date':
        range.startDate = getStartOfWeekDate();
        break;
      case 'month-to-date':
        range.startDate = getStartOfMonthDate();
        break;
      case 'last-7':
        range.startDate = new Date(Date.UTC(utcYear, utcMonth, utcDate - 6, 0, 0, 0, 0));
        break;
      case 'last-14':
        range.startDate = new Date(Date.UTC(utcYear, utcMonth, utcDate - 13, 0, 0, 0, 0));
        break;
      case 'last-30':
        range.startDate = new Date(Date.UTC(utcYear, utcMonth, utcDate - 29, 0, 0, 0, 0));
        break;
      case 'last-6-months':
        range.startDate = new Date(Date.UTC(utcYear, utcMonth - 6, utcDate, 0, 0, 0, 0));
        break;
      case 'last-year':
        range.startDate = new Date(Date.UTC(utcYear - 1, utcMonth, utcDate, 0, 0, 0, 0));
        break;
      case 'all-time':
        range.startDate = new Date(Date.UTC(2020, 0, 1, 0, 0, 0, 0));
        break;
    }

    setSelectedRange(range);
    onDateRangeChange?.(range);
    onPresetSelect?.(range);
  };

  const handleDateClick = (date: Date) => {
    const utcDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));
    
    if (!selectedRange.startDate || (selectedRange.startDate && selectedRange.endDate)) {
      const newRange = { startDate: utcDate, endDate: null };
      setSelectedRange(newRange);
      setActivePreset(null);
    } else {
      let newRange: DateRange;
      if (utcDate < selectedRange.startDate) {
        newRange = { startDate: utcDate, endDate: selectedRange.startDate };
      } else {
        newRange = { startDate: selectedRange.startDate, endDate: utcDate };
      }
      setSelectedRange(newRange);
      setActivePreset(null);
      onDateRangeChange?.(newRange);
    }
  };

  const isDateInRange = (date: Date) => {
    if (!selectedRange.startDate || !selectedRange.endDate) return false;
    const checkDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));
    const start = new Date(selectedRange.startDate);
    const end = new Date(selectedRange.endDate);
    return checkDate >= start && checkDate <= end;
  };

  const isDateStart = (date: Date) => {
    if (!selectedRange.startDate) return false;
    const checkDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));
    const start = new Date(selectedRange.startDate);
    return checkDate.getUTCFullYear() === start.getUTCFullYear() &&
           checkDate.getUTCMonth() === start.getUTCMonth() &&
           checkDate.getUTCDate() === start.getUTCDate();
  };

  const isDateEnd = (date: Date) => {
    if (!selectedRange.endDate) return false;
    const checkDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));
    const end = new Date(selectedRange.endDate);
    return checkDate.getUTCFullYear() === end.getUTCFullYear() &&
           checkDate.getUTCMonth() === end.getUTCMonth() &&
           checkDate.getUTCDate() === end.getUTCDate();
  };

  const isDateHovered = (date: Date) => {
    if (!hoveredDate || !selectedRange.startDate || selectedRange.endDate) return false;
    const checkDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));
    const start = new Date(selectedRange.startDate);
    const hover = new Date(Date.UTC(hoveredDate.getUTCFullYear(), hoveredDate.getUTCMonth(), hoveredDate.getUTCDate(), 0, 0, 0, 0));
    
    if (hover < start) {
      return checkDate >= hover && checkDate <= start;
    } else {
      return checkDate >= start && checkDate <= hover;
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const year = prev.getUTCFullYear();
      const month = prev.getUTCMonth();
      if (direction === 'prev') {
        return new Date(Date.UTC(year, month - 1, 1));
      } else {
        return new Date(Date.UTC(year, month + 1, 1));
      }
    });
  };

  const renderCalendarMonth = (monthDate: Date, showLeftArrow: boolean, showRightArrow: boolean) => {
    const year = monthDate.getUTCFullYear();
    const month = monthDate.getUTCMonth();
    const firstDay = new Date(Date.UTC(year, month, 1));
    const lastDay = new Date(Date.UTC(year, month + 1, 0));
    const startOffset = firstDay.getUTCDay();
    const daysInMonth = lastDay.getUTCDate();

    const days: (Date | null)[] = [];
    
    for (let i = 0; i < startOffset; i++) {
      days.push(null);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(Date.UTC(year, month, day)));
    }

    while (days.length % 7 !== 0) {
      days.push(null);
    }

    return (
      <div className='core-date-dropdown__month'>
        <div className='core-date-dropdown__month-header'>
          {showLeftArrow && (
            <button 
              className='core-date-dropdown__nav-btn'
              onClick={() => navigateMonth('prev')}
              aria-label="Previous month"
            >
              <ChevronLeft size={16} />
            </button>
          )}
          <span className='core-date-dropdown__month-title'>
            {MONTH_NAMES[month]} {year}
          </span>
          {showRightArrow && (
            <button 
              className='core-date-dropdown__nav-btn'
              onClick={() => navigateMonth('next')}
              aria-label="Next month"
            >
              <ChevronRight size={16} />
            </button>
          )}
        </div>
        <div className='core-date-dropdown__weekdays'>
          {DAY_LABELS.map(day => (
            <div key={day} className='core-date-dropdown__weekday'>{day}</div>
          ))}
        </div>
        <div className='core-date-dropdown__days'>
          {days.map((date, index) => {
            if (!date) {
              return <div key={`empty-${index}`} className='core-date-dropdown__day--empty' />;
            }

            const isStart = isDateStart(date);
            const isEnd = isDateEnd(date);
            const isSelected = isStart || isEnd;
            const isInRange = isDateInRange(date) && !isSelected; // Don't mark selected dates as in-range
            const isHovered = isDateHovered(date);
            const today = new Date();
            const isToday = date.getUTCFullYear() === today.getUTCFullYear() &&
                           date.getUTCMonth() === today.getUTCMonth() &&
                           date.getUTCDate() === today.getUTCDate();

            const dayClasses = [
              'core-date-dropdown__day',
              isInRange && 'core-date-dropdown__day--in-range',
              isSelected && 'core-date-dropdown__day--selected',
              isHovered && 'core-date-dropdown__day--hovered',
              isToday && 'core-date-dropdown__day--today'
            ].filter(Boolean).join(' ');

            return (
              <button
                key={`${date.getUTCMonth()}-${date.getUTCDate()}`}
                className={dayClasses}
                onClick={() => handleDateClick(date)}
                onMouseEnter={() => setHoveredDate(date)}
                onMouseLeave={() => setHoveredDate(null)}
              >
                {date.getUTCDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const nextMonth = new Date(Date.UTC(
    currentMonth.getUTCFullYear(),
    currentMonth.getUTCMonth() + 1,
    1
  ));

  return (
    <DropdownContainer
      isOpen={isOpen}
      onClose={onClose}
      anchorRef={anchorRef}
      position="right"
      direction="down"
      variant="default"
      size="medium"
      className={cx('core-date-dropdown', className)}
      closeOnScroll={false}
      closeOnResize={false}
    >
      <div className='core-date-dropdown__content'>
        {showPresets && (
          <div className='core-date-dropdown__presets'>
            {PRESET_RANGES.map(preset => {
              const presetClasses = [
                'core-date-dropdown__preset',
                activePreset === preset.id && 'core-date-dropdown__preset--active'
              ].filter(Boolean).join(' ');
              
              return (
                <button
                  key={preset.id}
                  className={presetClasses}
                  onClick={() => handlePresetClick(preset.id)}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>
        )}
        
        <div className='core-date-dropdown__calendars'>
          <div className='core-date-dropdown__months'>
            {renderCalendarMonth(currentMonth, true, false)}
            {renderCalendarMonth(nextMonth, false, true)}
          </div>
        </div>
      </div>
    </DropdownContainer>
  );
}

